// Hundred charts (22 Oct 2026, Year 2): the numbers 1 to 100 in rows of
// ten, or whole rows of it (say 21 to 60). Year 1 counts to 120 (24 Oct
// 2026), so a chart may run on past 100 in whole rows, up to 120. Some
// squares are shaded and some numbers are hidden behind a "?". The hidden
// numbers are never written or spoken, and which squares are shaded is
// never spoken: finding them is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type HundredChartSpec = {
  type: 'hundred_chart';
  // The first and last number shown: whole rows of ten, within 1 to 120.
  from: number;
  to: number;
  shaded: number[];
  hidden: number[];
};

// With more "?" than this the chart stops being a chart to read.
const MAX_HIDDEN = 20;
// The last number a chart may show.
const HIGHEST = 120;

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

// A list of different numbers on the chart, smallest first; null if any is
// off the chart, not whole, or given twice.
function readCells(value: unknown, from: number, to: number): number[] | null {
  const list = value ?? [];
  if (!Array.isArray(list)) return null;
  if (!list.every((cell) => isWholeIn(cell, from, to))) return null;
  const cells = list as number[];
  if (new Set(cells).size !== cells.length) return null;
  return [...cells].sort((a, b) => a - b);
}

export function readHundredChart(
  record: Record<string, unknown>,
): HundredChartSpec | null {
  const from = record.from ?? 1;
  const to = record.to ?? 100;
  if (!isWholeIn(from, 1, HIGHEST - 9) || !isWholeIn(to, 10, HIGHEST)) {
    return null;
  }
  const first = from as number;
  const last = to as number;
  // Whole rows only, so every number sits under the number ten less.
  if (first % 10 !== 1 || last % 10 !== 0 || first > last) return null;
  const shaded = readCells(record.shaded, first, last);
  const hidden = readCells(record.hidden, first, last);
  if (shaded === null || hidden === null) return null;
  if (hidden.length > MAX_HIDDEN) return null;
  // A shaded square may also be hidden, as a "?" on a shaded square.
  return { type: 'hundred_chart', from: first, to: last, shaded, hidden };
}

// What a screen reader is told in place of the chart: where it starts and
// ends, and that some squares are shaded or hidden, never which. When the
// first or last number is itself hidden, neither end is said. A chart that
// runs on past 100 is "a number chart": it is no longer a hundred chart.
export function describeHundredChart(spec: HundredChartSpec): string {
  const name = spec.to > 100 ? 'number chart' : 'hundred chart';
  const whole = spec.from === 1 && spec.to >= 100;
  const endHidden =
    spec.hidden.includes(spec.from) || spec.hidden.includes(spec.to);
  const chart = endHidden
    ? whole
      ? `A ${name}`
      : `Part of a ${name}`
    : whole
      ? `A ${name} from 1 to ${String(spec.to)}`
      : `Part of a ${name}, from ${String(spec.from)} to ${String(spec.to)}`;
  const marks = [
    ...(spec.shaded.length > 0 ? ['shaded squares'] : []),
    ...(spec.hidden.length > 0 ? ['question marks for hidden numbers'] : []),
  ];
  return marks.length === 0
    ? `${chart}, in rows of ten.`
    : `${chart}, in rows of ten, with ${marks.join(' and ')}.`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type HundredChartCell = {
  value: number;
  x: number;
  y: number;
  shaded: boolean;
  hidden: boolean;
};

export type HundredChartPicture = {
  cells: HundredChartCell[];
  cell: number;
  width: number;
  height: number;
};

export const HUNDRED_CHART_CELL = 32;
const EDGE = 8;

export function hundredChartPicture(
  spec: HundredChartSpec,
): HundredChartPicture {
  const cell = HUNDRED_CHART_CELL;
  const shaded = new Set(spec.shaded);
  const hidden = new Set(spec.hidden);
  const cells = Array.from({ length: spec.to - spec.from + 1 }, (_, index) => {
    const value = spec.from + index;
    return {
      value,
      x: EDGE + (index % 10) * cell,
      y: EDGE + Math.floor(index / 10) * cell,
      shaded: shaded.has(value),
      hidden: hidden.has(value),
    };
  });
  return {
    cells,
    cell,
    width: cell * 10 + EDGE * 2,
    height: cell * Math.ceil(cells.length / 10) + EDGE * 2,
  };
}
